import { Clock, MapPin, ArrowUpRight, BookOpen } from "lucide-react";
import { Reveal } from "./Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { offlineCourses, onlineCourses } from "../data/courses";

const CurriculumDialog = ({ course }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        data-testid={`course-curriculum-btn-${course.id}`}
        className="mt-3 inline-flex items-center gap-2 font-heading font-bold text-sm border border-[#0E0E0E] text-[#0E0E0E] px-6 py-3 rounded-xl hover:bg-[#0E0E0E] hover:text-[#E7CE96] transition-colors duration-300"
      >
        <BookOpen size={15} /> View Curriculum
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white rounded-2xl" data-testid={`course-curriculum-dialog-${course.id}`}>
      <DialogHeader>
        <DialogTitle className="font-heading font-extrabold text-2xl text-[#0E0E0E] pr-6">
          {course.title}: Curriculum
        </DialogTitle>
        <p className="font-heading text-sm font-bold text-[#8A7440]">
          {course.subtitle} · {course.mode}
          {course.mode.includes("Offline") && " · Dates: To Be Announced"}
        </p>
      </DialogHeader>
      <div className="flex flex-col gap-7 mt-2">
        {course.curriculum.map((d) => (
          <div key={d.day}>
            <h4 className="font-heading font-extrabold text-lg text-white bg-[#0E0E0E] rounded-lg px-4 py-2 w-fit">{d.day}</h4>
            <div className="mt-4 flex flex-col gap-5">
              {d.blocks.map((b) => (
                <div key={b.heading}>
                  <p className="font-heading font-bold text-sm tracking-[0.08em] uppercase text-[#8A7440]">{b.heading}</p>
                  {b.text ? (
                    <p className="mt-2.5 text-[15px] text-[#57534E] leading-relaxed">{b.text}</p>
                  ) : (
                    <ul className="mt-2.5 flex flex-col gap-1.5">
                      {b.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-[15px] text-[#57534E]">
                          <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#C6A45C]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {course.outro && (
          <p className="text-[15px] text-[#57534E] leading-relaxed border-t border-[#E8E4DC] pt-5">{course.outro}</p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const CourseCard = ({ course, delay }) => (
  <Reveal delay={delay} className="course-card group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`course-card-${course.id}`}>
    <div className={`course-img-wrap overflow-hidden ${course.fit === "contain" ? "h-72 bg-[#0E0E0E]" : course.fit === "banner" ? "aspect-[16/9]" : "h-52"}`}>
      <img src={course.image} alt={course.title} className={`w-full h-full ${course.fit === "contain" ? "object-contain" : "object-cover"}`} />
    </div>
    <div className="p-7 flex flex-col flex-1">
      <div className="flex items-center justify-between">
        <span className="font-heading font-extrabold text-xl text-[#C6A45C]">{course.number}</span>
        <span className="flex items-center gap-1.5 font-heading text-[11px] font-bold tracking-[0.1em] uppercase text-[#57534E]">
          <MapPin size={12} className="text-[#C6A45C]" /> {course.mode}
        </span>
      </div>
      <h4 className="font-heading font-bold text-xl lg:text-2xl text-[#0E0E0E] mt-4">{course.title}</h4>
      {course.subtitle && (
        <p className="mt-1.5 font-heading text-sm font-semibold text-[#8A7440]">{course.subtitle}</p>
      )}
      <p className="inline-flex items-center gap-2 mt-3 font-heading text-sm font-bold text-[#E7CE96] bg-[#0E0E0E] rounded-lg px-3 py-1.5 w-fit" data-testid={`course-duration-${course.id}`}>
        <Clock size={14} /> {course.duration}
      </p>
      {course.mode.includes("Offline") && (
        <p className="mt-2.5 font-heading text-xs font-bold tracking-[0.1em] uppercase text-[#8A7440]" data-testid={`course-dates-${course.id}`}>
          Dates: To Be Announced
        </p>
      )}
      <p className="mt-4 text-[15px] text-[#57534E] leading-relaxed flex-1">{course.description}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {course.tags.map((t) => (
          <span key={t} className="font-heading text-[10px] font-bold tracking-[0.08em] uppercase px-3 py-1.5 bg-[#FAFAF8] text-[#0E0E0E] rounded-full">
            {t}
          </span>
        ))}
      </div>
      <a
        href={`https://wa.me/919321967864?text=${encodeURIComponent(`Hi Dr. Kshama, I'm interested in the ${course.title}.`)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`course-enquire-${course.id}`}
        className="mt-7 inline-flex items-center justify-center gap-2 font-heading font-bold text-sm bg-[#C6A45C] text-[#0E0E0E] px-6 py-3 rounded-xl hover:bg-[#A88743] transition-colors duration-300"
      >
        Enquire Now <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
      {course.curriculum && <CurriculumDialog course={course} />}
    </div>
  </Reveal>
);

const CourseGroup = ({ eyebrow, title, subtitle, courses, testId }) => (
  <div data-testid={testId}>
    <Reveal>
      <span className="font-heading text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-[#C6A45C]">{eyebrow}</span>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mt-3">
        <h3 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0E0E0E] leading-[1.1]">{title}</h3>
        <p className="text-base text-[#57534E] max-w-md lg:text-right">{subtitle}</p>
      </div>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 mt-10">
      {courses.map((c, i) => (
        <CourseCard key={c.id} course={c} delay={i * 0.12} />
      ))}
    </div>
  </div>
);

export const Courses = () => (
  <section id="courses" className="py-20 lg:py-28 bg-[#F3F1EC]" data-testid="courses-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-20 lg:gap-24">
      <Reveal className="max-w-3xl mx-auto text-center">
        <span className="font-heading text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-[#C6A45C]">The Curriculum</span>
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.12] text-[#0E0E0E]">
          Courses designed for <em className="font-bold text-[#C6A45C]">real clinical practice</em>.
        </h2>
      </Reveal>

      <CourseGroup
        eyebrow="In-Person · Across India"
        title="Offline Programs"
        subtitle="Intensive hands-on programs with focused mentorship, practical training and individual attention across Mumbai, Delhi, Pune, Hyderabad and other cities in India."
        courses={offlineCourses}
        testId="offline-courses-group"
      />

      <CourseGroup
        eyebrow="Digital · Worldwide"
        title="Online Programs"
        subtitle="Learn Dr. Kshama's complete workflows live and online, from anywhere in the world."
        courses={onlineCourses}
        testId="online-courses-group"
      />
    </div>
  </section>
);
